import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { t, resolveInitialLang, isSupportedLang, isRtlLang, urlLangPrefix, stripLangPrefix } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Sprachauflösung (siehe lib/i18n/index.js):
//   0. Sprach-Präfix im Pfad (/de, /el, …) — bindend, überstimmt ALLES,
//      auch die Profilsprache: Die URL ist der Inhalt, den man teilt.
//   1. Profilsprache des eingeloggten Nutzers  → Effekt unten, einmal pro Login
//   2. localStorage  → resolveInitialLang()
//   3. URL (?lang=)  → resolveInitialLang()
//   4. Browsersprache → resolveInitialLang()
//   5. Standard "en"
// Ein expliziter Wechsel wird sofort in localStorage gespeichert und — falls
// eingeloggt — zusätzlich ins Profil geschrieben (PATCH /auth/me), damit die
// Wahl auf anderen Geräten erhalten bleibt.
// ---------------------------------------------------------------------------

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { user } = useAuth() || {};
  const [lang, setLangState] = useState(resolveInitialLang);
  // Merkt sich, für welchen Nutzer die Profilsprache schon angewendet wurde,
  // damit ein manueller Wechsel nicht beim nächsten /auth/me-Refresh
  // zurückgesetzt wird.
  const profileAppliedFor = useRef(null);
  // Einmal beim Laden bestimmt, nie danach: Der Router ist mit diesem Präfix
  // als basename gemountet — es kann sich ohne volles Neuladen nicht ändern.
  const urlPinned = useRef(urlLangPrefix() !== null);

  // <html lang="…"> und dir="ltr|rtl" aktuell halten (Screenreader, SEO, RTL).
  // ar/he schalten die gesamte Anwendung auf RTL um (siehe config/market.js).
  //
  // lang NUR, wenn keine Inhaltssprache gesetzt ist: Die einsprachigen
  // Ratgeber-Seiten tragen deutschen Inhalt in beliebiger UI-Sprache und
  // setzen ueber Seo.jsx (htmlLang) das data-Attribut. Dieser Effekt laeuft
  // beim ersten Rendern NACH dem der Seiten (Effekte von innen nach aussen) —
  // ohne die Ruecksicht ueberschriebe er lang="de" genau im Snapshot.
  useEffect(() => {
    if (!document.documentElement.dataset.inhaltssprache) {
      document.documentElement.lang = lang;
    }
    document.documentElement.dir = isRtlLang(lang) ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    try {
      localStorage.setItem("lang", lang);
    } catch {
      /* Private-Mode ohne localStorage */
    }
  }, [lang]);

  // Stufe 1: Profilsprache übernehmen — genau einmal pro Login. NICHT auf
  // Präfix-URLs: Wer /de/impressum öffnet, soll Deutsch sehen, auch wenn im
  // Profil Russisch steht. Sonst zeigte dieselbe URL eingeloggten und
  // ausgeloggten Besuchern verschiedene Sprachen (Audit-Befund 2.2).
  useEffect(() => {
    if (user && user.id) {
      if (profileAppliedFor.current !== user.id) {
        profileAppliedFor.current = user.id;
        if (!urlPinned.current && isSupportedLang(user.language)) setLangState(user.language);
      }
    } else {
      profileAppliedFor.current = null;
    }
  }, [user]);

  // Expliziter Sprachwechsel durch den Nutzer.
  const setLang = useCallback(
    (code) => {
      if (!isSupportedLang(code)) return;
      setLangState(code);
      if (user && user.id) {
        // Best effort — ein Fehler darf den Wechsel im UI nicht blockieren.
        api.patch("/auth/me", { language: code }).catch(() => {});
      }
    },
    [user],
  );

  // Sprachwechsel MIT Navigation auf die Sprach-URL — der Weg für die
  // sichtbaren Umschalter (Navbar, Footer). /pakete + „Deutsch" → /de/pakete;
  // /de/pakete + „English" → /pakete. Query und Hash bleiben erhalten.
  //
  // Volles Neuladen ist hier kein Unfall, sondern nötig: Der Router ist mit
  // dem alten Präfix als basename gemountet, und ein basename wechselt nicht
  // zur Laufzeit. Erst speichern (localStorage synchron, Profil mit kurzem
  // Zeitfenster), dann navigieren — sonst risse die Navigation den
  // PATCH-Request ab und andere Geräte behielten die alte Sprache.
  const switchLanguage = useCallback(
    async (code) => {
      if (!isSupportedLang(code)) return;
      try {
        localStorage.setItem("lang", code);
      } catch {
        /* Private-Mode ohne localStorage */
      }
      if (user && user.id) {
        await Promise.race([
          api.patch("/auth/me", { language: code }).catch(() => {}),
          new Promise((r) => setTimeout(r, 600)),
        ]);
      }
      const rest = stripLangPrefix(window.location.pathname);
      const prefix = code === "en" ? "" : `/${code}`;
      const ziel = `${prefix}${rest === "/" && prefix ? "" : rest}${window.location.search}${window.location.hash}`;
      window.location.assign(ziel || "/");
    },
    [user],
  );

  const tr = useCallback((path, vars) => t(lang, path, vars), [lang]);

  const value = useMemo(() => ({ lang, setLang, switchLanguage, t: tr }), [lang, setLang, switchLanguage, tr]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLang = () => useContext(LanguageContext);
